import _ from "lodash";
import React, { useState, useRef } from "react";
import { useI18n } from "Hooks";

import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined, EyeHiddenOutlined } from "@toddle-edu/ds-icons";

import { getModuleAccessLevels } from "StaffPermissions/modules/utils";
import { MODULES } from "StaffPermissions/modules/constants";
import classes from "./ModuleAccessInfoPopup.scss";

import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { colors } from "Constants";
import classNames from "classnames";
import ACLStore from "lib/aclStore";

const getArrowDegree = position => {
  switch (position) {
    case "top":
      return 225;
    case "right":
      return 315;
    case "bottom":
      return 45;
    case "left":
      return 135;
    default:
      return 45;
  }
};

const styles = {
  arrowStyle: ({ position }) => ({
    backgroundColor: colors.surfaceWhite,
    width: "20px",
    height: "20px",
    transform: `rotate(${getArrowDegree(position)}deg)`,
    borderLeft: `1px solid ${colors.borderSubtle}`,
    borderTop: `1px solid ${colors.borderSubtle}`,
  }),
  overlayInnerStyle: {
    maxWidth: "240px",
    textAlign: "center",
  },
};

const ModuleAccessInfo = props => {
  const {
    module,
    curriculumAcronym,
    curriculumType,
    overlayContainerWithMargin,
    behaviourNomenclatureConstants,
  } = props;
  const { t } = useI18n();
  const moduleAccessLevels = getModuleAccessLevels(module);
  const moduleInfo = MODULES[module];
  const { label } = moduleInfo;

  const overlayContainerStyles = classNames({
    [classes.overlayContainer]: true,
    [classes.horizontalMargin]: overlayContainerWithMargin,
  });

  return (
    <div className={overlayContainerStyles}>
      {_.map(moduleAccessLevels, accessLevel => {
        const {
          id,
          Icon,
          descriptionLocale,
          labelLocale,
          descriptionLocaleProps = [],
          getDescriptionLocale,
        } = accessLevel;

        let localesText = _.get(
          descriptionLocale,
          curriculumType,
          descriptionLocale
        );

        if (_.isFunction(getDescriptionLocale)) {
          localesText = getDescriptionLocale(ACLStore);
        }

        const localeParams = {
          curriculum: curriculumAcronym,
          incident: behaviourNomenclatureConstants.INCIDENT,
          action: behaviourNomenclatureConstants.ACTION,
          level: behaviourNomenclatureConstants.LEVEL,
          module: behaviourNomenclatureConstants.MODULE,
        };

        const description = !_.isEmpty(descriptionLocaleProps)
          ? t(localesText, _.pick(localeParams, descriptionLocaleProps))
          : t(localesText);

        return (
          <div
            key={id}
            className={classNames("text-textSubtle", classes.overlayInfoItem)}
          >
            <Icon className="text-iconSubtle" />
            <div className={classes.infoItemText}>
              <div className="text-body-1 line-height-24">
                {t(labelLocale)}:
              </div>
              <div className="text-body-3 line-height-16">{description}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ModuleAccessLevelInfoPopup = props => {
  const { column, containerRef, behaviourNomenclatureConstants } = props;
  const {
    id,
    metadata: { curriculumAcronym = "", curriculumType } = {},
    isDisabled,
    iconVariant = "dark",
    subIconVariant = "on",
    positions = ["bottom"],
    align = "center",
    overlayContainerWithMargin = true,
  } = column;
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useI18n(["common"]);

  const iconButtonRef = useRef(null);

  const clickMeButtonRef = useRef(null);
  const showPopover = () => setIsVisible(true);
  const hidePopover = () => setIsVisible(false);
  return (
    <>
      {isDisabled ? (
        <Tooltip
          tooltip={t("common:staff_permission_disable_module", {
            curriculum: curriculumAcronym,
          })}
          placement="bottom"
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <IconButton
            icon={<EyeHiddenOutlined variant="on" />}
            variant="dark"
            size="medium"
          />
        </Tooltip>
      ) : null}
      <Popover
        isOpen={isVisible}
        padding={10}
        positions={positions}
        align={align}
        onClickOutside={hidePopover}
        containerClassName={classes.popupContainerClassName}
        ref={clickMeButtonRef}
        containerParent={containerRef?.current}
        content={({ position, childRect, popoverRect }) =>
          position ? (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowSize={10}
              arrowStyle={styles.arrowStyle({
                position,
              })}
            >
              <ModuleAccessInfo
                module={id}
                curriculumAcronym={curriculumAcronym}
                overlayContainerWithMargin={overlayContainerWithMargin}
                curriculumType={curriculumType}
                behaviourNomenclatureConstants={behaviourNomenclatureConstants}
              />
            </ArrowContainer>
          ) : null
        }
      >
        <IconButton
          icon={<InformationOutlined variant={subIconVariant} />}
          variant={iconVariant}
          ref={iconButtonRef}
          onMouseOver={showPopover}
          onMouseOut={hidePopover}
        />
      </Popover>
    </>
  );
};

export default ModuleAccessLevelInfoPopup;
